import { useId, useRef, useState } from 'react'
import { CurrencyInput } from '@/components/CurrencyInput'
import { managerialIsoDate } from '@/lib/managerial-calendar'
import type { CreateOperatingCostEntryInput } from '@/lib/api/operating-costs-admin.service'
import type { OperatingCostCategory, OperatingCostEntry } from '@/types/api'
import {
  formatOperatingCostAmount,
  parseAmountInput,
  parseRateInput,
} from '../lib/operating-costs'
import {
  nextOperatingCostMonth,
  operatingCostMonthCount,
  operatingCostMonthIndex,
  operatingCostMonthMax,
} from '../lib/entry-months'
import styles from '../CustosOperacionaisPage.module.css'

type Props = {
  category: OperatingCostCategory
  isSaving: boolean
  mode?: 'create' | 'edit' | 'duplicate'
  entry?: OperatingCostEntry
  onSubmit: (input: CreateOperatingCostEntryInput) => Promise<boolean>
  onCancel?: () => void
}

export function OperatingCostEntryForm({
  category,
  isSaving,
  mode = 'create',
  entry,
  onSubmit,
  onCancel,
}: Props) {
  const id = useId()
  const submitting = useRef(false)
  const [subtitle, setSubtitle] = useState(entry?.subtitle ?? '')
  const [amount, setAmount] = useState(entry?.amount == null ? '' : String(entry.amount))
  const [rate, setRate] = useState(
    entry?.rate == null ? '' : String(entry.rate).replace('.', ',')
  )
  const [month, setMonth] = useState(() =>
    mode === 'duplicate'
      ? nextOperatingCostMonth(entry?.competence_month)
      : mode === 'edit'
        ? (entry?.competence_month ?? '')
        : managerialIsoDate().slice(0, 7)
  )
  const [end, setEnd] = useState(month)
  const [error, setError] = useState<string | null>(null)
  const isPercent = category.value_type === 'percent'
  const isRange = category.kind === 'fixed' && !isPercent && mode !== 'edit'
  const count = isRange ? operatingCostMonthCount(month, end) : 1
  const parsedAmount = parseAmountInput(amount)

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (isSaving || submitting.current) return
    const name = subtitle.trim()
    if (!name) {
      setError('Informe o nome do lançamento.')
      return
    }
    const value = isPercent ? parseRateInput(rate) : parsedAmount
    if (value === null) {
      setError(
        isPercent ? 'Informe um percentual entre 0 e 100.' : 'Informe o valor em reais.'
      )
      return
    }
    if (!isPercent && month && operatingCostMonthIndex(month) === null) {
      setError('Informe um mês de competência válido.')
      return
    }
    if (isRange && count === null) {
      setError('Informe o mês inicial e um mês final igual ou posterior a ele.')
      return
    }
    setError(null)
    submitting.current = true
    try {
      const ok = await onSubmit(
        isPercent
          ? { subtitle: name, rate: value }
          : {
              subtitle: name,
              amount: value,
              competenceMonth: month || null,
              ...(isRange && end !== month ? { competenceMonthEnd: end } : {}),
            }
      )
      if (ok && mode === 'create') {
        setSubtitle('')
        setAmount('')
        setRate('')
      }
    } finally {
      submitting.current = false
    }
  }

  return (
    <form
      className={styles.costEntryForm}
      onSubmit={(event) => {
        void submit(event)
      }}
      aria-label={
        mode === 'edit'
          ? 'Editar lançamento'
          : mode === 'duplicate'
            ? 'Duplicar lançamento'
            : 'Novo lançamento'
      }
    >
      <label className={styles.field} htmlFor={`${id}-name`}>
        Nome do lançamento
        <input
          id={`${id}-name`}
          className={styles.input}
          value={subtitle}
          maxLength={120}
          disabled={isSaving}
          placeholder="Ex.: Aluguel da sala"
          onChange={(event) => setSubtitle(event.target.value)}
        />
      </label>
      <label className={styles.field} htmlFor={`${id}-value`}>
        {isPercent ? 'Percentual' : 'Valor'}
        {isPercent ? (
          <input
            id={`${id}-value`}
            className={styles.input}
            inputMode="decimal"
            value={rate}
            disabled={isSaving}
            onChange={(event) => setRate(event.target.value)}
          />
        ) : (
          <CurrencyInput
            id={`${id}-value`}
            value={amount}
            disabled={isSaving}
            onChange={setAmount}
          />
        )}
      </label>
      {!isPercent ? (
        <label className={styles.field} htmlFor={`${id}-month`}>
          {isRange ? 'Mês inicial' : 'Competência'}
          <input
            id={`${id}-month`}
            className={styles.input}
            type="month"
            min="2000-01"
            max={operatingCostMonthMax()}
            value={month}
            disabled={isSaving}
            onChange={(event) => {
              const next = event.target.value
              setMonth(next)
              if (end === month) setEnd(next)
            }}
          />
        </label>
      ) : null}
      {isRange ? (
        <label className={styles.field} htmlFor={`${id}-end`}>
          Mês final
          <input
            id={`${id}-end`}
            className={styles.input}
            type="month"
            min={month || '2000-01'}
            max={operatingCostMonthMax()}
            value={end}
            disabled={isSaving}
            onChange={(event) => setEnd(event.target.value)}
          />
        </label>
      ) : null}
      <div className={styles.entryActions}>
        <button type="submit" className={styles.primaryButton} disabled={isSaving}>
          {isSaving
            ? 'Salvando...'
            : mode === 'edit'
              ? 'Salvar'
              : mode === 'duplicate'
                ? 'Criar cópia'
                : 'Adicionar'}
        </button>
        {onCancel ? (
          <button
            type="button"
            className={styles.ghostButton}
            disabled={isSaving}
            onClick={onCancel}
          >
            Cancelar
          </button>
        ) : null}
      </div>
      {isRange ? (
        <p className={styles.entryFormHint} role="status">
          {count !== null && parsedAmount !== null
            ? `${count} lançamento${count === 1 ? '' : 's'} de ${formatOperatingCostAmount(parsedAmount)}. Total do período: ${formatOperatingCostAmount((Math.round(parsedAmount * 100) * count) / 100)}.`
            : 'Escolha os meses inicial e final. O valor será lançado em cada mês do intervalo.'}{' '}
          Cada mês pode ser editado ou excluído separadamente.
        </p>
      ) : null}
      {mode === 'duplicate' ? (
        <p className={styles.entryFormHint}>
          A cópia é um novo lançamento. Você pode alterar nome, valor e competência antes
          de salvar.
        </p>
      ) : null}
      {error ? (
        <p className={styles.inlineError} role="alert">
          {error}
        </p>
      ) : null}
    </form>
  )
}
